/**
 * BETA — État de rotation report.
 *
 * The EtatRotationPage (client/src/pages/decision/EtatRotationPage.jsx) POSTs
 * { date_debut, date_fin, categorie?, format } and, for format="json", reads
 *   payload.rows[]   → { produit_id, nom, stock_actuel, quantite_vendue,
 *                        velocity, rotation, statut }
 *   payload.summary  → { total_produits, rotation_moyenne, rapides, dormants }
 * For format="pdf"|"xlsx" it expects a downloadable blob (we return a text
 * payload so the browser still produces a file in demo mode).
 */
import { todayYmd } from "./_utils.js";

// Velocity → rotation status. Mirrors the page's STATUS_TONES keys.
function statutOf(rotation) {
  if (rotation >= 6) return "RAPIDE";
  if (rotation >= 3) return "NORMAL";
  if (rotation > 0) return "LENT";
  return "DORMANT";
}

// Build the per-product rotation rows over [debut, fin], optional category.
function buildRows(store, body) {
  const debut = body.date_debut || null;
  const fin = body.date_fin ? body.date_fin + "T23:59:59.999Z" : null;
  const cat = body.categorie || null;

  // window length in days (for velocity), min 1.
  let days = 30;
  if (debut && body.date_fin) {
    days = Math.max(
      1,
      Math.round((new Date(body.date_fin) - new Date(debut)) / 86400000) + 1,
    );
  }

  // sold qty per product within the window
  const sold = new Map();
  for (const v of store.data.ventes) {
    if (v.statut !== "VALIDEE") continue;
    const t = v.date_vente || "";
    if (debut && t.slice(0, 10) < debut) continue;
    if (fin && t > fin) continue;
    for (const d of v.details || []) {
      sold.set(d.produit_id, (sold.get(d.produit_id) || 0) + (d.quantite || 0));
    }
  }

  const rows = store.data.produits
    .filter((p) => p.statut_produit !== "BLOQUE_CACHE")
    .filter((p) => !cat || p.type_produit === cat)
    .map((p) => {
      const vendus = sold.get(p.id) || 0;
      const stock = p.stock_actuel || 0;
      const velocity = +(vendus / days).toFixed(2);
      // annualised turnover ratio vs current stock
      const annualUnits = velocity * 365;
      const rotation = +(annualUnits / Math.max(1, stock)).toFixed(2);
      return {
        produit_id: p.id,
        nom: p.nom_commercial,
        nom_commercial: p.nom_commercial,
        dci: p.dci || "",
        categorie: p.type_produit,
        stock_actuel: stock,
        quantite_vendue: vendus,
        vendus,
        velocity,
        velocite: velocity,
        rotation,
        taux_rotation: rotation,
        statut: statutOf(rotation),
      };
    })
    .sort((a, b) => b.rotation - a.rotation);

  const rapides = rows.filter((r) => r.statut === "RAPIDE").length;
  const dormants = rows.filter((r) => r.statut === "DORMANT").length;
  const rotationMoy =
    rows.length > 0
      ? +(rows.reduce((s, r) => s + r.rotation, 0) / rows.length).toFixed(2)
      : 0;

  return {
    rows,
    summary: {
      total_produits: rows.length,
      rotation_moyenne: rotationMoy,
      rapides,
      dormants,
    },
  };
}

export function register(route) {
  route("POST", "/rapports/etat-rotation", (ctx) => {
    const body = ctx.body || {};
    const fmt = (body.format || "json").toLowerCase();
    const { rows, summary } = buildRows(ctx.store, body);

    if (fmt === "json") {
      return { rows, summary };
    }

    // pdf / xlsx / html → return a text payload the page downloads as a blob.
    // (Real binary generation is out of scope for the in-browser demo.)
    if (fmt === "xlsx" || fmt === "csv") {
      const header = "Produit;DCI;Catégorie;Stock;Vendus;Vélocité/j;Rotation;Statut";
      const lines = rows.map(
        (r) =>
          `${r.nom};${r.dci};${r.categorie};${r.stock_actuel};${r.quantite_vendue};${r.velocity};${r.rotation};${r.statut}`,
      );
      return { status: 200, data: [header, ...lines].join("\n") };
    }

    // pdf / html → printable HTML document
    const trs = rows
      .map(
        (r) =>
          `<tr><td>${r.nom}</td><td>${r.dci}</td><td style="text-align:right">${r.stock_actuel}</td><td style="text-align:right">${r.quantite_vendue}</td><td style="text-align:right">${r.velocity}</td><td style="text-align:right">${r.rotation}</td><td>${r.statut}</td></tr>`,
      )
      .join("");
    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>État de rotation — Démo</title>
<style>body{font-family:system-ui;padding:24px;color:#111}h1{font-size:20px;margin:0 0 4px}h2{font-size:13px;color:#666;margin:0 0 16px;font-weight:500}table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}th{background:#f5f5f5;font-weight:600}.badge{display:inline-block;padding:3px 8px;background:#fde68a;color:#92400e;border-radius:4px;font-size:11px;font-weight:700}.s{margin:0 0 16px;font-size:12px;color:#444}</style>
</head><body>
<h1>État de rotation des produits <span class="badge">DÉMO</span></h1>
<h2>PHARMAKON · ${body.date_debut || "—"} → ${body.date_fin || todayYmd()}</h2>
<p class="s">${summary.total_produits} produits · rotation moyenne ${summary.rotation_moyenne} · ${summary.rapides} rapides · ${summary.dormants} dormants</p>
<table><thead><tr><th>Produit</th><th>DCI</th><th>Stock</th><th>Vendus</th><th>Vélocité/j</th><th>Rotation/an</th><th>Statut</th></tr></thead>
<tbody>${trs}</tbody></table>
</body></html>`;
    return { status: 200, data: html };
  });

  route("GET", "/rapports/laboratoires", (ctx) => {
    // Distinct laboratoire labels for the report filter dropdown.
    const ids = new Set(ctx.store.data.produits.map((p) => p.laboratoire_id).filter(Boolean));
    const labos = (ctx.store.data.laboratoires || [])
      .filter((l) => ids.has(l.id))
      .map((l) => ({ id: l.id, nom: l.nom }));
    return labos.length ? labos : [{ id: "lab-saidal", nom: "Saidal" }];
  });
}
